import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { useT } from "@/contexts/LanguageContext";
import { Calendar, User, Mail, Phone, Filter, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const t = useT();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const STATUS_MAP = {
    pending: { labelKey: "admin_pending", className: "bg-amber-100 text-amber-800 border-amber-200" },
    confirmed: { labelKey: "admin_confirmed", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    cancelled: { labelKey: "admin_cancelled", className: "bg-red-100 text-red-800 border-red-200" },
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data);
    } catch {
      toast.error(t("admin_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status`, { status: newStatus });
      toast.success(`${t("admin_status_updated")}: ${t(STATUS_MAP[newStatus].labelKey)}`);
      fetchBookings();
      if (selectedBooking?.id === bookingId) setSelectedBooking((prev) => ({ ...prev, status: newStatus }));
    } catch {
      toast.error(t("admin_status_error"));
    }
  };

  const filteredBookings = statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter);

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-[#F3F5F7]" data-testid="admin-page">
      <header className="bg-[#002FA7] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="AtoB Logistics" className="h-10 w-auto brightness-0 invert" />
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t("admin_title")}</h1>
              <span className="text-xs text-white/60">AtoB Logistics</span>
            </div>
          </div>
          <Button onClick={fetchBookings} variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white rounded-sm text-sm" data-testid="refresh-bookings-button">
            <RefreshCw className="w-4 h-4 mr-2" />{t("admin_refresh")}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { labelKey: "admin_total", value: bookings.length, color: "bg-white" },
            { labelKey: "admin_pending", value: bookings.filter(b => b.status === "pending").length, color: "bg-amber-50 border-amber-200" },
            { labelKey: "admin_confirmed", value: bookings.filter(b => b.status === "confirmed").length, color: "bg-emerald-50 border-emerald-200" },
            { labelKey: "admin_cancelled", value: bookings.filter(b => b.status === "cancelled").length, color: "bg-red-50 border-red-200" },
          ].map((stat) => (
            <div key={stat.labelKey} className={`p-4 border border-border ${stat.color}`}>
              <div className="text-2xl font-black text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stat.value}</div>
              <div className="text-xs text-[#475569] uppercase tracking-[0.1em]">{t(stat.labelKey)}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-6 bg-white p-4 border border-border">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#475569]" />
            <span className="text-sm font-medium text-[#0A0A0A]">{t("admin_filter")}</span>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-sm" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin_filter_all")}</SelectItem>
              <SelectItem value="pending">{t("admin_pending")}</SelectItem>
              <SelectItem value="confirmed">{t("admin_confirmed")}</SelectItem>
              <SelectItem value="cancelled">{t("admin_cancelled")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-border" data-testid="bookings-table">
          {loading ? (
            <div className="py-20 text-center text-[#475569] text-sm">{t("admin_loading")}</div>
          ) : filteredBookings.length === 0 ? (
            <div className="py-20 text-center text-[#475569] text-sm" data-testid="no-bookings">{t("admin_no_bookings")}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F3F5F7]">
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_nr")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_vehicle")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_customer")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_period")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_price")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_status")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em] font-semibold">{t("admin_col_action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.map((booking) => (
                  <TableRow key={booking.id} className="cursor-pointer hover:bg-[#F3F5F7]/50">
                    <TableCell className="text-xs font-mono text-[#475569]">{booking.id.slice(0, 8)}...</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-[#0A0A0A]">{booking.vehicle_name}</div>
                      <div className="text-xs text-[#475569]">{booking.vehicle_type === "lkw" ? "LKW" : "Transporter"}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-[#0A0A0A]">{booking.customer_name}</div>
                      <div className="text-xs text-[#475569]">{booking.customer_email}</div>
                    </TableCell>
                    <TableCell className="text-sm text-[#0A0A0A]">{formatDate(booking.start_date)} - {formatDate(booking.end_date)}</TableCell>
                    <TableCell className="text-sm font-semibold text-[#002FA7]">{booking.total_price.toFixed(2)} EUR</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_MAP[booking.status]?.className || ""} rounded-sm text-xs font-medium`}>
                        {t(STATUS_MAP[booking.status]?.labelKey || booking.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-[#002FA7] hover:text-[#002FA7] hover:bg-[#002FA7]/10" onClick={() => setSelectedBooking(booking)}>
                        {t("admin_details")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t("admin_detail_title")}</DialogTitle>
            <DialogDescription>{t("admin_detail_booking")} #{selectedBooking?.id.slice(0, 8)}</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">{t("admin_col_vehicle")}</div>
                  <span className="text-sm font-medium">{selectedBooking.vehicle_name}</span>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569]">{t("admin_col_period")}</div>
                  <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#002FA7]" /><span className="text-sm">{formatDate(selectedBooking.start_date)} - {formatDate(selectedBooking.end_date)}</span></div>
                </div>
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center gap-2"><User className="w-4 h-4 text-[#475569]" /><span className="text-sm">{selectedBooking.customer_name}</span></div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#475569]" /><span className="text-sm">{selectedBooking.customer_email}</span></div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#475569]" /><span className="text-sm">{selectedBooking.customer_phone}</span></div>
              </div>
              <div className="border-t border-border pt-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#475569]">{selectedBooking.total_days} {selectedBooking.total_days === 1 ? "Tag" : "Tage"}</div>
                  <div className="text-xl font-black text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{selectedBooking.total_price.toFixed(2)} EUR</div>
                </div>
                <Badge className={`${STATUS_MAP[selectedBooking.status]?.className || ""} rounded-sm text-xs font-medium`}>
                  {t(STATUS_MAP[selectedBooking.status]?.labelKey || selectedBooking.status)}
                </Badge>
              </div>
              <div className="border-t border-border pt-4">
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-[#475569] mb-2">{t("admin_change_status")}</div>
                <div className="flex gap-2">
                  {selectedBooking.status !== "confirmed" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs" onClick={() => updateStatus(selectedBooking.id, "confirmed")} data-testid="confirm-booking-button">{t("admin_confirm")}</Button>
                  )}
                  {selectedBooking.status !== "cancelled" && (
                    <Button size="sm" variant="destructive" className="rounded-sm text-xs" onClick={() => updateStatus(selectedBooking.id, "cancelled")} data-testid="cancel-booking-button">{t("admin_cancel")}</Button>
                  )}
                  {selectedBooking.status !== "pending" && (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => updateStatus(selectedBooking.id, "pending")} data-testid="reset-booking-button">{t("admin_reset")}</Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
